import type { Point } from "./types.ts";
import { registerText } from "../../i18n/textRegistry.ts";

const COORDINATE_SECTIONS = ["COORDINATES", "VERTICES", "POLYGONS", "SYMBOLS"];

/** All 4 sections share the identical `[name, xStr, yStr]` row shape — a dedicated bulk pass rather than looping the one-point-at-a-time writers, which would re-clone each section once per point instead of once total. */
export function applyCoordinateTransform(
  raw: ReadonlyMap<string, string[][]>,
  transform: (p: Point) => Point,
  /** Must drop a row with a non-finite input/output coordinate rather than write the literal string "NaN" — a malformed `.inp` coordinate real SWMM rejects on load. */
  warnings?: Set<string>
): Map<string, string[][]> {
  const next = new Map(raw);
  for (const section of COORDINATE_SECTIONS) {
    const rows = raw.get(section);
    if (!rows || rows.length === 0) continue;
    const out: string[][] = [];
    for (const row of rows) {
      const x = Number(row[1]);
      const y = Number(row[2]);
      const point =
        Number.isFinite(x) && Number.isFinite(y) ? transform({ x, y }) : { x: NaN, y: NaN };
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        warnings?.add(
          registerText(
            "modelWarning.nonFiniteCoordinate",
            `[${section}] row "${row[0]}" has a non-finite coordinate (${row[1]}, ${row[2]}).`,
            { section, name: row[0], x: row[1], y: row[2] }
          )
        );
        continue;
      }
      out.push([row[0], String(point.x), String(point.y)]);
    }
    next.set(section, out);
  }
  return next;
}
